<template>
    <div>
        <div
            class="search-tabs-wrapper"
            :class="{'search-tabs-wrapper_no-tabs': activeProducts.length + tabLinks.length === 1}"
        >
            <v-theme-provider
                class="search-tabs"
                :theme="theme"
                with-background
                style="background: transparent !important"
            >
                <v-tabs
                    v-if="activeProducts.length > 1 || tabLinks.length > 0"
                    ref="tabs"
                    v-model="productTab"
                    :show-arrows="!isMobile"
                    color="white"
                    bg-color="rgba(0, 0, 0, 0.6)"
                    slider-color="primary"
                    :theme="theme"
                    :stacked="isMobile"
                    class="search-tabs-wrapper__tabs"
                    @update:model-value="change"
                >
                    <v-tab
                        v-for="(product, index) in activeProducts"
                        :key="product + '_tab'"
                        :value="index"
                        :style="isMobile ? 'min-width: 60px; padding: 0 6px' : ''"
                    >
                        <span class="search-tabs__sm-label hidden-md-and-up" style="font-size: 8px; margin-top: 2px">
                            {{ $t(products[product].title) }}
                        </span>
                        <v-icon class="me-1">
                            {{ products[product].ico }}
                        </v-icon>
                        <span class="hidden-sm-and-down">{{ $t(products[product].title) }}</span>
                    </v-tab>
                    <search-form-tab-link
                        v-for="tab in tabLinks"
                        :key="tab.name"
                        :name="tab.name"
                        :href="tab.href"
                        :ico="tab.ico"
                    />
                </v-tabs>
                <choose-agent-dialog v-if="chooseAgentDialogEnabled" ref="chooseAgentDialog" />
                <v-tabs-window
                    v-model="productTab"
                    class="search-tabs__component overflow-visible"
                    :class="{'is-animating': isTabAnimating}"
                >
                    <v-tabs-window-item v-for="(product, index) in activeProducts" :key="index" :value="index">
                        <component
                            :is="products[product].form"
                            :variant="products[product].variant"
                            :product-name="products[product].title"
                            :city-id="cityId"
                            :class="{'product-tabs': activeProducts.length > 1}"
                            :data-cy="products[product]['data-cy']"
                            :choose-agent-dialog-valid="chooseAgentDialogRef?.valid"
                            :choose-agent-dialog-validate="chooseAgentDialogValidate"
                        />
                    </v-tabs-window-item>
                    <div v-if="$conf.public.layout.searchForm.light" class="pa-3 pt-0 d-block d-md-none">
                        <v-btn
                            v-if="isShowNewReservationRequest"
                            size="large"
                            variant="text"
                            color="primary"
                            class="reservation-btn full-width"
                            data-cy="new-reservation-request"
                            @click="newOrderRequestModal = true"
                        >
                            <span>
                                {{ $t('new_reservation_request') }}
                            </span>
                        </v-btn>
                        <new-order-request-modal v-model="newOrderRequestModal" theme="light" />
                    </div>
                </v-tabs-window>
            </v-theme-provider>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Watch} from 'vue-facing-decorator'
    import HotelSearchForm from '~src/components/search/forms/hotelSearchForm.src'
    import TransferSearchForm from '~src/components/transfers/search/forms/transferSearchForm.src'
    import ActivitySearchForm from '~src/components/activities/search/forms/activitySearchForm.src'
    import FlightSearchForm from '~src/components/flights/search/forms/flightSearchForm.src'
    import InsuranceSearchForm from '@/components/insurance/search/forms/InsuranceSearchForm.vue'
    import VisaSearchForm from '@/components/visa/search/forms/VisaSearchForm.vue'
    import PackagesSearchForm from '@/components/packages/search/forms/PackagesSearchForm.vue'
    import CarsSearchForm from '~src/components/carsRent/search/forms/CarsSearchForm.vue'
    import ExtraServiceSearchForm from '~src/components/extraServices/search/forms/extraServiceSearchForm.src'
    import TourSearchForm from '~src/components/tours/search/forms/TourSearchForm.vue'
    import SimpleTourSearchForm from '~src/components/simpleTours/search/forms/SimpleTourSearchForm.vue'
    import CruisesSearchForm from '~src/components/cruises/search/forms/CruisesSearchForm.vue'
    import SearchFormTabLink from '@/components/SearchFormTabLink.vue'
    import ChooseAgentDialog from '~src/components/ChooseAgentDialog.vue'
    import {AuthInfoMixin} from '~src/mixins/authInfoMixin.src'

    @Component({
        components: {
            ChooseAgentDialog,
            SearchFormTabLink,
            HotelSearchForm,
            TransferSearchForm,
            ActivitySearchForm,
            FlightSearchForm,
            PackagesSearchForm,
            CarsSearchForm,
            ExtraServiceSearchForm,
            TourSearchForm,
            SimpleTourSearchForm,
            CruisesSearchForm,
            InsuranceSearchForm,
            VisaSearchForm,
            NewOrderRequestModal: defineAsyncComponent(
                () => import('~src/components/account/modals/newOrderRequest/newOrderRequestModal.src')
            ),
        },
    })
    export class SearchFormTabs extends AuthInfoMixin {
        @VModel() productTab: number
        @Prop() cityId
        @Prop({default: false, type: Boolean}) chooseAgent

        chooseAgentDialogRef = null
        newOrderRequestModal = false
        isTabAnimating = false
        isTabAnimatingTimeout = null

        @Emit()
        change(index) {
            if (this.isTabAnimatingTimeout) clearTimeout(this.isTabAnimatingTimeout)
            this.isTabAnimating = true
            if (index !== 0 && this.$route.query.tab !== this.activeProducts[index]) {
                this.$router.push({name: 'home', query: {tab: this.activeProducts[index]}})
            } else if (index === 0) {
                this.$router.push({name: 'home'})
            }
            this.isTabAnimatingTimeout = setTimeout(() => {
                this.isTabAnimating = false
                this.isTabAnimatingTimeout = null
            }, 500)
            return this.activeProducts[index]
        }

        @Watch('locale')
        onLocaleChange() {
            this.$refs.tabs.onResize()
        }

        @Watch('chooseAgentDialogEnabled')
        chooseAgentDialogEnabledChange() {
            this.$nextTick(() => {
                this.chooseAgentDialogRef = this.$refs.chooseAgentDialog
            })
        }

        mounted() {
            this.chooseAgentDialogRef = this.$refs.chooseAgentDialog
        }

        get selectAgentEnabled() {
            if (!this.$conf.public.account.showCorporateClients && this.isAgency) {
                return false
            }

            return (
                this.isTO1 ||
                this.isTO2 ||
                (this.isB2B && this.$conf.public.account.showSelectAgentB2B && !this.isCorporate)
            )
        }

        get chooseAgentDialogEnabled() {
            return (
                this.selectAgentEnabled &&
                !this.selectedCompanyAndAgent &&
                !this.selectedPrivateClient &&
                this.chooseAgent
            )
        }

        get chooseAgentDialogValidate() {
            return false
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get tabLinks() {
            return []
        }
        get selectedCompanyAndAgent() {
            return persistentStore.selectedCompanyAndAgent
        }

        get selectedPrivateClient() {
            return persistentStore.selectedPrivateClient
        }

        get isB2B() {
            return authStore.hasCorporateAccess
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get products() {
            return {
                hotels: {
                    ico: 'mdi-office-building',
                    title: 'hotels',
                    form: resolveComponent('HotelSearchForm'),
                    'data-cy': 'hotel-search-form',
                },
                transfers: {
                    ico: 'mdi-bus',
                    title: 'transfers.transfers',
                    form: resolveComponent('TransferSearchForm'),
                    'data-cy': 'transfer-search-form',
                },
                activities: {
                    ico: 'mdi-lightning-bolt-outline',
                    title: 'activities.activities',
                    form: resolveComponent('ActivitySearchForm'),
                    'data-cy': 'activity-search-form',
                },
                flights: {
                    ico: 'mdi-airplane',
                    title: 'flights.flights',
                    form: resolveComponent('FlightSearchForm'),
                    'data-cy': 'flight-search-form',
                },
                packages: {
                    ico: 'mdi-ticket-confirmation-outline',
                    title: 'packages.packages',
                    form: resolveComponent('PackagesSearchForm'),
                    'data-cy': 'flight-hotel-search-form',
                },
                tours: {
                    ico: 'mdi-wallet-travel',
                    title: 'tours.tours',
                    form: resolveComponent('TourSearchForm'),
                    'data-cy': 'packages-search-form',
                },
                tour: {
                    ico: 'mdi-wallet-travel',
                    title: 'tours.simple_tours',
                    form: resolveComponent('SimpleTourSearchForm'),
                    'data-cy': 'simple-tour-form',
                },
                carsRent: {
                    ico: 'mdi-car',
                    title: 'carsRent.cars_rent',
                    form: resolveComponent('CarsSearchForm'),
                    'data-cy': 'car-search-form',
                },
                insurance: {
                    ico: 'mdi-umbrella',
                    title: 'insurance.insurance',
                    form: resolveComponent('InsuranceSearchForm'),
                    'data-cy': 'insurance-search-forms',
                },
                visa: {
                    ico: 'mdi-passport',
                    title: 'visa.visa',
                    form: resolveComponent('VisaSearchForm'),
                    'data-cy': 'visa-search-form',
                },
                extraServices: {
                    ico: 'mdi-star-box',
                    title: 'extraServices.extra_services',
                    form: resolveComponent('ExtraServiceSearchForm'),
                    'data-cy': 'extra-services-search-form',
                },
                conferenceHall: {
                    ico: 'mdi-monitor-dashboard',
                    title: 'extraServices.conference_hall',
                    form: resolveComponent('ExtraServiceSearchForm'),
                    variant: 'conference',
                    'data-cy': 'conference-hall-search-form',
                },
                catering: {
                    ico: 'mdi-cup',
                    title: 'extraServices.catering',
                    form: resolveComponent('ExtraServiceSearchForm'),
                    variant: 'catering',
                    'data-cy': 'catering-search-form',
                },
                cruises: {
                    ico: 'mdi-ferry',
                    title: 'cruises.cruises',
                    form: resolveComponent('CruisesSearchForm'),
                    'data-cy': 'cruises-search-form',
                },
            }
        }

        get theme() {
            return this.$conf.public.layout.searchForm.light ? 'light' : 'dark'
        }

        get isShowNewReservationRequest() {
            return this.$conf.public.account.newReservationRequest && !this.hideNewReservationRequest
        }

        get hideNewReservationRequest() {
            return false
        }
    }

    export default SearchFormTabs
</script>
